import {
    MISSING_POLICY,
    SCORING_TYPE,
    SET_AGGREGATION,
    AI_MATCH_LEVEL,
    OVERRIDE_MODE,
} from "./models/scoringModel";

/**
 * The backend serializes enums as PascalCase strings (global JsonStringEnumConverter()
 * in MLApi/Program.cs, shared by the whole API -- not worth changing for just this
 * feature). This package's UI/model layer uses the spec's own UPPER_SNAKE_CASE
 * vocabulary (VALUE/SET/RANGE/AI_MATCH etc.). These maps translate between the two
 * at the API boundary so neither host app has to duplicate the translation.
 */
const MISSING_POLICY_TO_API = {
    [MISSING_POLICY.IGNORE]: "Ignore",
    [MISSING_POLICY.ENTITY_ZERO]: "EntityZero",
    [MISSING_POLICY.TOTAL_ZERO]: "TotalZero",
    [MISSING_POLICY.DISQUALIFY]: "Disqualify",
};
const SCORING_TYPE_TO_API = {
    [SCORING_TYPE.VALUE]: "Value",
    [SCORING_TYPE.SET]: "Set",
    [SCORING_TYPE.RANGE]: "Range",
    [SCORING_TYPE.AI_MATCH]: "AiMatch",
};
const SET_AGGREGATION_TO_API = {
    [SET_AGGREGATION.SUM_VALUES]: "SumValues",
    [SET_AGGREGATION.MATCH_COUNT]: "MatchCount",
};
const AI_MATCH_LEVEL_TO_API = {
    [AI_MATCH_LEVEL.FULL_MATCH]: "FullMatch",
    [AI_MATCH_LEVEL.HIGH_MATCH]: "HighMatch",
    [AI_MATCH_LEVEL.PARTIAL_MATCH]: "PartialMatch",
    [AI_MATCH_LEVEL.LOW_MATCH]: "LowMatch",
    [AI_MATCH_LEVEL.NO_MATCH]: "NoMatch",
    [AI_MATCH_LEVEL.UNKNOWN]: "Unknown",
};
const OVERRIDE_MODE_TO_API = {
    [OVERRIDE_MODE.LOCKED]: "Locked",
    [OVERRIDE_MODE.ALLOW_RECRUITMENT_OVERRIDE]: "AllowRecruitmentOverride",
};

const invert = (map) => Object.fromEntries(Object.entries(map).map(([k, v]) => [v, k]));
const MISSING_POLICY_FROM_API = invert(MISSING_POLICY_TO_API);
const SCORING_TYPE_FROM_API = invert(SCORING_TYPE_TO_API);
const SET_AGGREGATION_FROM_API = invert(SET_AGGREGATION_TO_API);
const AI_MATCH_LEVEL_FROM_API = invert(AI_MATCH_LEVEL_TO_API);
const OVERRIDE_MODE_FROM_API = invert(OVERRIDE_MODE_TO_API);

/** Converts one criterion's `scoring` object (this package's shape) into the EntityScoringRequest/RecruitmentCriterionOverrideRequest wire shape. */
export function scoringConfigToApi(scoring) {
    if (!scoring) return null;
    return {
        criterionKey: scoring.criterionKey ?? undefined,
        scoringEnabled: !!scoring.scoringEnabled,
        maxScore: scoring.maxScore ?? null,
        description: scoring.description ?? "",
        missingPolicy: MISSING_POLICY_TO_API[scoring.missingPolicy] ?? "Ignore",
        scoringType: SCORING_TYPE_TO_API[scoring.scoringType] ?? "Value",
        // `points` (and `value`, on Value/Set rules) are non-nullable on the wire (ValueScoringRuleRequest /
        // RangeScoringRuleRequest / SetScoringRuleRequest / AiLevelPointsRequest all declare `decimal Points`,
        // not `decimal?`) -- a rule row the user added but hasn't finished filling in must never serialize
        // `null` there, or the whole save gets rejected with a 400 (see the incident this was fixed for:
        // a single incomplete rule anywhere in the form blocked saving the entire recruitment). `min`/`max`
        // on range rules stay nullable on the wire (an open-ended range is meaningful), so those are untouched.
        valueRules: (scoring.valueRules ?? []).map((r) => ({ value: r.value ?? "", points: r.points ?? 0, disqualify: !!r.disqualify })),
        rangeRules: (scoring.rangeRules ?? []).map((r) => ({ min: r.min, max: r.max, points: r.points ?? 0, disqualify: !!r.disqualify })),
        setRules: (scoring.setRules ?? []).map((r) => ({ value: r.value ?? "", points: r.points ?? 0 })),
        setAggregation: SET_AGGREGATION_TO_API[scoring.setAggregation] ?? "SumValues",
        capAtMaxScore: scoring.capAtMaxScore ?? true,
        aiCriterion: scoring.aiCriterion ?? null,
        aiLevels: (scoring.aiLevels ?? []).map((l) => ({ level: AI_MATCH_LEVEL_TO_API[l.level] ?? "Unknown", points: l.points ?? 0 })),
        overrideMode: scoring.overrideMode ? OVERRIDE_MODE_TO_API[scoring.overrideMode] : null,
    };
}

/** Converts an EntityScoringResponse (or EffectiveCriterion.scoring) from the wire shape back into this package's model. */
export function scoringConfigFromApi(apiScoring) {
    if (!apiScoring) return null;
    return {
        criterionKey: apiScoring.criterionKey ?? null,
        scoringEnabled: !!apiScoring.scoringEnabled,
        maxScore: apiScoring.maxScore ?? null,
        description: apiScoring.description ?? "",
        missingPolicy: MISSING_POLICY_FROM_API[apiScoring.missingPolicy] ?? MISSING_POLICY.IGNORE,
        scoringType: SCORING_TYPE_FROM_API[apiScoring.scoringType] ?? SCORING_TYPE.VALUE,
        valueRules: (apiScoring.valueRules ?? []).map((r) => ({ value: r.value, points: r.points, disqualify: !!r.disqualify })),
        rangeRules: (apiScoring.rangeRules ?? []).map((r) => ({ min: r.min, max: r.max, points: r.points, disqualify: !!r.disqualify })),
        setRules: (apiScoring.setRules ?? []).map((r) => ({ value: r.value, points: r.points })),
        setAggregation: SET_AGGREGATION_FROM_API[apiScoring.setAggregation] ?? SET_AGGREGATION.SUM_VALUES,
        capAtMaxScore: apiScoring.capAtMaxScore ?? true,
        aiCriterion: apiScoring.aiCriterion ?? "",
        aiLevels: (apiScoring.aiLevels ?? []).map((l) => ({ level: AI_MATCH_LEVEL_FROM_API[l.level] ?? AI_MATCH_LEVEL.UNKNOWN, points: l.points })),
        overrideMode: apiScoring.overrideMode ? OVERRIDE_MODE_FROM_API[apiScoring.overrideMode] : null,
        scoringVersion: apiScoring.scoringVersion ?? 1,
    };
}

export function evaluationStatusFromApi(status) {
    const map = { InProgress: "IN_PROGRESS", Scored: "SCORED", NeedsReview: "NEEDS_REVIEW", Disqualified: "DISQUALIFIED" };
    return map[status] ?? status;
}

export function aiMatchLevelFromApi(level) {
    return level ? (AI_MATCH_LEVEL_FROM_API[level] ?? AI_MATCH_LEVEL.UNKNOWN) : null;
}

export function scoringModelSourceFromApi(source) {
    return source === "Scenario" ? "scenario" : "recruitment";
}
